import { useCallback, useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import Modal from '../ui/Modal';
import Badge from '../ui/Badge';
import { api } from '../../lib/api';
import { Field, TextInput, TextArea, Select, MoneyInput, History, Empty, usd, fmtDate, today, btnCls, btn2Cls, thCls, tdCls, MOVE_LABEL } from './shared';

// Stock at Pinnacle, split into Concria-owned (consignment: CCS pays when it is sold or
// used) and CCS-owned. Using consignment stock on a job creates a payable line to Concria.

const OWNER = { concria: 'Concria-owned (consignment)', ccs: 'CCS-owned' };
const fmtQty = (n) => Number(n).toLocaleString('en-US', { maximumFractionDigits: 3 });

export default function StockTab({ onChanged }) {
  const [data, setData] = useState(null);
  const [item, setItem] = useState(null);
  const [move, setMove] = useState(null);
  const [error, setError] = useState('');
  const load = useCallback(async () => {
    try { setData(await api.get('/intercompany/stock')); setError(''); } catch (e) { setError(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const done = () => { setItem(null); setMove(null); load(); onChanged(); };
  const removeMove = async (m) => {
    if (!window.confirm('Delete this movement? A payable line it created (if still just claimed) is removed too.')) return;
    try { await api.delete(`/intercompany/stock/moves/${m.id}`); done(); } catch (e) { setError(e.message); }
  };

  if (!data) return error ? <p className="text-sm text-rose-600">{error}</p> : <p className="text-sm text-[var(--text-secondary)]">Loading...</p>;
  const itemById = new Map(data.items.map((i) => [i.id, i]));
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-[var(--text-secondary)]">Counts are only what has been recorded. Enter Pinnacle's monthly count as an opening count or an adjustment.</p>
        <div className="flex gap-2">
          <button className={btn2Cls} onClick={() => setItem({})}><Plus size={14} /> Add product</button>
          <button className={btnCls} disabled={!data.items.length} onClick={() => setMove({})}><Plus size={14} /> Record movement</button>
        </div>
      </div>
      {error && <p className="text-sm text-rose-600">{error}</p>}
      {['concria', 'ccs'].map((owner) => {
        const items = data.items.filter((i) => i.owner === owner);
        return (
          <div key={owner}>
            <h3 className="mb-2 font-semibold text-[var(--text-primary)]">{OWNER[owner]}</h3>
            {items.length === 0 ? <Empty>None recorded.</Empty> : (
              <div className="overflow-x-auto rounded-xl border border-[var(--border)] bg-white dark:bg-slate-900">
                <table className="w-full min-w-[640px] text-sm">
                  <thead className="bg-gray-50 dark:bg-slate-800"><tr>
                    <th className={thCls}>Product</th><th className={`${thCls} text-right`}>On hand</th><th className={`${thCls} text-right`}>Pallets</th>
                    <th className={thCls}>Location</th><th className={`${thCls} text-right`}>Unit cost</th>
                  </tr></thead>
                  <tbody>
                    {items.map((i) => (
                      <tr key={i.id} className="cursor-pointer border-t border-[var(--border)] hover:bg-gray-50 dark:hover:bg-slate-800" onClick={() => setItem(i)}>
                        <td className={tdCls}><div className="text-[var(--text-primary)]">{i.product}{i.color ? `, ${i.color}` : ''}</div>{i.note && <div className="text-xs text-[var(--text-secondary)]">{i.note}</div>}</td>
                        <td className={`${tdCls} text-right whitespace-nowrap`}>{i.move_count ? `${fmtQty(i.on_hand)} ${i.unit}` : <span className="text-[var(--text-secondary)]">no count</span>}{i.last_move && <div className="text-xs text-[var(--text-secondary)]">as of {fmtDate(i.last_move)}</div>}</td>
                        <td className={`${tdCls} text-right`}>{i.pallets ?? ''}</td>
                        <td className={tdCls}>{i.location}</td>
                        <td className={`${tdCls} text-right`}>{i.unit_cost_cents != null ? usd(i.unit_cost_cents) : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
      <div>
        <h3 className="mb-2 font-semibold text-[var(--text-primary)]">Movements</h3>
        {data.moves.length === 0 ? <Empty>No movements yet.</Empty> : (
          <ul className="divide-y divide-[var(--border)] rounded-xl border border-[var(--border)] bg-white text-sm dark:bg-slate-900">
            {data.moves.map((m) => {
              const it = itemById.get(m.item_id);
              return (
                <li key={m.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
                  <div>
                    <div>{fmtDate(m.move_date)} · {MOVE_LABEL[m.kind]} · {m.kind === 'adjust' && Number(m.adjust_sign) === -1 ? '-' : ''}{fmtQty(m.qty)} {it?.unit} {it ? `${it.product}${it.color ? `, ${it.color}` : ''}` : ''}</div>
                    <div className="text-xs text-[var(--text-secondary)]">{[m.job, m.reference, m.note].filter(Boolean).join(' · ')}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {m.payable_line_id && <Badge variant="warning">Payable to Concria</Badge>}
                    <button className="text-xs text-rose-600" onClick={() => removeMove(m)}>delete</button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      {item && <ItemModal item={item.id ? item : null} onClose={() => setItem(null)} onSaved={done} />}
      {move && <MoveModal items={data.items} onClose={() => setMove(null)} onSaved={done} />}
    </div>
  );
}

const ITEM_FIELDS = ['product', 'sku', 'color', 'owner', 'unit', 'unit_weight', 'location', 'pallets', 'unit_cost_cents', 'source_url', 'note'];

function ItemModal({ item, onClose, onSaved }) {
  const [f, setF] = useState(() => (item ? Object.fromEntries(ITEM_FIELDS.map((k) => [k, item[k] ?? ''])) : { owner: 'concria', unit: 'bag', location: 'Pinnacle, Virginia Beach' }));
  const [error, setError] = useState('');
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => {
    try {
      const body = {};
      for (const k of ITEM_FIELDS) {
        const v = f[k];
        if (item && String(v ?? '') === String(item[k] ?? '')) continue;
        if (!item && (v === '' || v == null)) continue;
        body[k] = v === '' ? null : v;
      }
      if (item) await api.patch(`/intercompany/stock/items/${item.id}`, { ...body, revision: item.updated_at });
      else await api.post('/intercompany/stock/items', body);
      onSaved();
    } catch (e) { setError(e.message); }
  };
  const remove = async () => {
    try { await api.delete(`/intercompany/stock/items/${item.id}`); onSaved(); } catch (e) { setError(e.message); }
  };
  return (
    <Modal title={item ? 'Product' : 'Add a product'} onClose={onClose}>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Product"><TextInput value={f.product} onChange={set('product')} /></Field>
        <Field label="Colour"><TextInput value={f.color} onChange={set('color')} /></Field>
        <Field label="Owner"><Select value={f.owner} onChange={set('owner')} options={OWNER} /></Field>
        <Field label="SKU / Concria code"><TextInput value={f.sku} onChange={set('sku')} /></Field>
        <Field label="Unit (bag, pail, lb, sf...)"><TextInput value={f.unit} onChange={set('unit')} /></Field>
        <Field label="Unit weight"><TextInput value={f.unit_weight} onChange={set('unit_weight')} placeholder="25 kg" /></Field>
        <Field label="Location"><TextInput value={f.location} onChange={set('location')} /></Field>
        <Field label="Pallets"><TextInput value={f.pallets} onChange={set('pallets')} inputMode="decimal" /></Field>
        <Field label="Owed to Concria per unit used (USD)"><MoneyInput cents={f.unit_cost_cents === '' ? null : f.unit_cost_cents} onChange={set('unit_cost_cents')} /></Field>
        <Field label="Source link"><TextInput value={f.source_url} onChange={set('source_url')} /></Field>
        <Field label="Note" className="sm:col-span-2"><TextArea value={f.note} onChange={set('note')} rows={2} /></Field>
      </div>
      {error && <p className="mt-3 text-sm text-rose-600">{error}</p>}
      <div className="mt-4 flex gap-2">
        <button className={btnCls} onClick={save}>{item ? 'Save' : 'Add'}</button>
        {item && <button className={btn2Cls} onClick={remove}>Delete</button>}
      </div>
      {item && <History table="stock_items" id={item.id} />}
    </Modal>
  );
}

function MoveModal({ items, onClose, onSaved }) {
  const [f, setF] = useState({ item_id: items[0]?.id, kind: 'out_job', move_date: today(), qty: '', adjust_sign: 1 });
  const [payAmt, setPayAmt] = useState(null);
  const [error, setError] = useState('');
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const it = items.find((i) => i.id === f.item_id);
  const payableDefault = it?.owner === 'concria' && f.kind === 'out_job';
  const [payable, setPayable] = useState(null);
  const isPayable = payable ?? payableDefault;
  const save = async () => {
    try {
      const body = { item_id: f.item_id, kind: f.kind, move_date: f.move_date, qty: f.qty, job: f.job || null, reference: f.reference || null, note: f.note || null };
      if (f.kind === 'adjust') body.adjust_sign = Number(f.adjust_sign);
      if (it?.owner === 'concria' && ['out_job', 'sample'].includes(f.kind)) body.payable = isPayable;
      if (isPayable && payAmt) body.payable_amount_cents = payAmt;
      await api.post('/intercompany/stock/moves', body);
      onSaved();
    } catch (e) { setError(e.message); }
  };
  return (
    <Modal title="Record a stock movement" onClose={onClose}>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Product" className="sm:col-span-2">
          <Select value={f.item_id} onChange={set('item_id')} options={Object.fromEntries(items.map((i) => [i.id, `${i.product}${i.color ? `, ${i.color}` : ''} (${OWNER[i.owner]})`]))} />
        </Field>
        <Field label="Movement"><Select value={f.kind} onChange={set('kind')} options={MOVE_LABEL} /></Field>
        <Field label="Date"><TextInput type="date" value={f.move_date} onChange={set('move_date')} /></Field>
        <Field label={`Quantity (${it?.unit || 'units'})`}><TextInput value={f.qty} onChange={set('qty')} inputMode="decimal" /></Field>
        {f.kind === 'adjust' && <Field label="Adjustment"><Select value={String(f.adjust_sign)} onChange={set('adjust_sign')} options={{ 1: 'Add to count', '-1': 'Take from count' }} /></Field>}
        <Field label="Job / customer"><TextInput value={f.job} onChange={set('job')} /></Field>
        <Field label="Reference (PO, Pinnacle SO)"><TextInput value={f.reference} onChange={set('reference')} /></Field>
        <Field label="Note" className="sm:col-span-2"><TextArea value={f.note} onChange={set('note')} rows={2} /></Field>
        {it?.owner === 'concria' && ['out_job', 'sample'].includes(f.kind) && (
          <div className="rounded-lg border border-[var(--border)] p-3 sm:col-span-2">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={isPayable} onChange={(e) => setPayable(e.target.checked)} /> CCS owes Concria for this usage (adds a ledger line)</label>
            {isPayable && (
              <div className="mt-2">
                <Field label={it.unit_cost_cents ? `Amount (blank = ${usd(it.unit_cost_cents)} per ${it.unit} x quantity)` : 'Amount owed to Concria (USD)'}>
                  <MoneyInput cents={null} onChange={setPayAmt} />
                </Field>
              </div>
            )}
          </div>
        )}
      </div>
      {error && <p className="mt-3 text-sm text-rose-600">{error}</p>}
      <button className={`${btnCls} mt-4`} onClick={save}>Record</button>
    </Modal>
  );
}
